// Routes disabled in the US school platform (NEXT_PUBLIC_IS_SCHOOL_PLATFORM=true). Plain CommonJS
// because `next.config.js` has to require() it and cannot resolve `~/` or TypeScript;
// `schoolPlatformRoutes.d.ts` types it for `middleware.ts` and the tests.
//
// Two ways to disable, with two different enforcement points — they are NOT interchangeable:
//   * HIDDEN_ROUTE_* → `next.config.js` turns each source into a `beforeFiles` rewrite to /404.
//     The default: as far as a school user is concerned the page does not exist.
//   * HOME_FALLBACK_ROUTE_* → `middleware.ts` redirects to `/`. For pages a school user can still
//     plausibly reach (an in-app link, a bookmark) and where `/` answers their intent — in this
//     edition `/` is the student/teacher role selection. It has to be middleware rather than a
//     `redirects()` entry, because a config redirect leaves a client-side `router.push` showing the
//     old URL with the new page's components; middleware.ts explains the mechanism in full.
//     Middleware runs before rewrites, so never list the same path in both.
//
// Two rules when editing:
//   1. Only list a PREFIX when EVERY page under that directory is disabled — verify with
//      `find pages/<dir> -name '*.tsx'`. /get-started, /campaigns and /onboarding keep routes
//      alongside disabled ones, so those are exact paths.
//   2. Do NOT add `locale: false` in next.config.js. Next prefixes each `source` with
//      `/:nextInternalLocale(en|no|uk)`, which is what makes one entry cover /no/... and /uk/... too.
//      The HOME_FALLBACK lists need no such treatment: middleware strips the locale before matching.

/**
 * Top-level keys of `PartnerDetails` (src/campaigns/consts/partners.consts.ts), each reachable at
 * both /p/<key> and /campaigns/<key>. Blocking as a prefix also covers group partners such as
 * /campaigns/dnb/saga-private-banking. Duplicated by hand because next.config.js cannot import TS —
 * src/campaigns/__tests__/schoolPlatformRoutes.test.ts fails if it drifts from PartnerDetails.
 */
const CAMPAIGN_PARTNER_KEYS = [
  'cox',
  'appalachian',
  'acom',
  'peachjar',
  'atxmom',
  'elevkalenderen',
  'cfa',
  'cellcom',
  'amazon',
  'stackbyme',
  'dnb',
]

/**
 * Directories where every page is hidden. Each yields two rewrite sources: the bare path (the index
 * route) and `/:path*` (everything below it).
 */
const HIDDEN_ROUTE_PREFIXES = [
  '/tutor-on-demand', // ToD marketing pages + blog + campaign mirror
  '/booking', // tutoring checkout
  '/tutor', // tutor panel
  '/student', // ToD student panel (appointments/my-tutor/receipts) — NOT /dashboard
  '/session', // live tutoring session
  '/subscription', // subscription + checkout
  '/adyen', // payment provider callback
  '/vipps', // payment provider callback
  '/purchase-agreement', // tutoring purchase agreement
  '/p', // partner landing pages (DynamicCampaign, driven by PartnerDetails)
  '/family', // school seats are not family accounts; covers the Stripe manage-subscription subtree
  '/mathmagic', // whole charity section incl. the donate flow — pending PO confirmation
  '/campaigns/tutor-on-demand', // Sanity mirror of the ToD marketing pages
  '/campaigns/christmas2024', // campaign checkout flow
  '/campaigns/back-to-school-offers', // redirects into /tutor-on-demand
  ...CAMPAIGN_PARTNER_KEYS.map((key) => `/campaigns/${key}`),
]

/** Individual hidden pages in directories that also contain kept routes. */
const HIDDEN_ROUTE_PATHS = [
  '/get-started/price',
  '/get-started/direct/price',
  // Parked onboarding A/B arm: no test is running, so middleware rewrites nothing and this and
  // /get-started/control both render the same funnel as /get-started/intro. Kept hidden, and
  // /get-started/control kept reachable, exactly as before that test ended.
  '/get-started/variant',
  '/get-started/us/payment',
  // Checkout / subscription entry points
  '/onboarding/subscribe',
  '/onboarding/upgrade-account', // family-pack upgrade; its back button targets /get-started/price
  '/onboarding-flow/payment',
  // '/register/subscription-packages' used to sit here; the whole /register tree is now a
  // HOME_FALLBACK prefix, and a path in both lists would take the redirect anyway.
  // Partner campaign pricing pages
  '/campaigns/accenture',
  '/campaigns/knowit',
  '/campaigns/linje5',
  // Campaign leaderboards rank participants across organisations by nickname, avatar and XP. Exact
  // paths, not a '/leaderboard' prefix — pages/leaderboard/index.tsx is the school league and stays.
  '/leaderboard/accenture',
  '/leaderboard/linje5',
  // Norwegian exam archive. NO-only content and untranslated in English.
  '/exams',
  // Referral (Invite Friends, Earn 250 XP). Solicits the student directly.
  '/referral',
  '/cookie-policy',
]

/** Every rewrite `source` needed to block the above. */
const HIDDEN_ROUTE_SOURCES = [
  ...HIDDEN_ROUTE_PREFIXES.flatMap((prefix) => [prefix, `${prefix}/:path*`]),
  ...HIDDEN_ROUTE_PATHS,
]

/**
 * Directories redirected to `/` in full. `/register` is the b2c signup funnel — a school seat is
 * provisioned by the teacher, never self-served — and `pages/register` holds only `index.tsx`, so
 * the prefix covers the tree without hiding a route that has to stay.
 */
const HOME_FALLBACK_ROUTE_PREFIXES = ['/register']

/**
 * Individual pages redirected to `/`. `/login` is the b2c email/SSO sign-in; schools sign in at
 * /school/student/login or /school/teacher/login, both reached from the role selection at `/`.
 */
const HOME_FALLBACK_ROUTE_PATHS = ['/login']

const matchesRoute = (pathname, paths, prefixes) =>
  paths.includes(pathname) || prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))

/** True when `pathname` is rewritten to /404 in the school platform. */
const isHiddenInSchoolPlatform = (pathname) => matchesRoute(pathname, HIDDEN_ROUTE_PATHS, HIDDEN_ROUTE_PREFIXES)

/** True when `pathname` is redirected to `/` in the school platform. */
const isRedirectedToHomeInSchoolPlatform = (pathname) =>
  matchesRoute(pathname, HOME_FALLBACK_ROUTE_PATHS, HOME_FALLBACK_ROUTE_PREFIXES)

module.exports = {
  CAMPAIGN_PARTNER_KEYS,
  HIDDEN_ROUTE_PREFIXES,
  HIDDEN_ROUTE_PATHS,
  HIDDEN_ROUTE_SOURCES,
  HOME_FALLBACK_ROUTE_PREFIXES,
  HOME_FALLBACK_ROUTE_PATHS,
  isHiddenInSchoolPlatform,
  isRedirectedToHomeInSchoolPlatform,
}
